import {
  AGENT_MODES,
  DEFAULT_AGENT_HIGHEST_MODE,
  INTELLIGENCE_LEVELS,
  MODULE_AGENT_CONFIG_KEYS,
  agentModeSchema,
  intelligenceLevelSchema,
  type AgentMode,
  type EffectiveModule,
  type IntelligenceLevel,
  type ModuleDefinition,
  type ModuleReason,
  type ModuleState,
} from '@traceio-org/platform-contracts';
import type { RecordStatusTone } from '@traceio-org/ui';

/**
 * Module rules Trace Admin uses to explain and offer actions. They only shape the screens: platform-api
 * computes the effective state and validates every change (dependencies, entitlement, release, version).
 */

/** The part of a module key after its product: enterprise.quoting is quoting. */
export function moduleFunction(key: string): string {
  const dot = key.indexOf('.');
  return dot === -1 ? key : key.slice(dot + 1);
}

/** The functions whose work has an intelligence level (entitlements design, section 2). */
export const LEVELLED_FUNCTIONS: readonly string[] = ['quoting', 'negotiation', 'booking', 'tracking'];

/** The configuration key of the level (contracts 0.7.0; part of every module's agent setting since 0.24.0). */
export const INTELLIGENCE_LEVEL_KEY = 'intelligenceLevel';

/**
 * Whether a module's configuration has an intelligence level: its published configuration schema says so
 * (contracts 0.7.0 configSchema); a definition without one falls back to the four functions that have it.
 */
export function hasIntelligenceLevel(definition: Pick<ModuleDefinition, 'key' | 'configSchema'>): boolean {
  const properties = definition.configSchema?.['properties'];
  if (properties !== null && typeof properties === 'object') return INTELLIGENCE_LEVEL_KEY in properties;
  return LEVELLED_FUNCTIONS.includes(moduleFunction(definition.key));
}

const LEVEL_TEXT: Readonly<Record<IntelligenceLevel, { label: string; description: string }>> = {
  steady: { label: 'Steady', description: 'Lighter work per action, so each action uses fewer tokens.' },
  seasoned: { label: 'Seasoned', description: 'The reference level.' },
  strategist: {
    label: 'Strategist',
    description: 'Deeper work per action, so each action uses more tokens.',
  },
  mastermind: { label: 'Mastermind', description: 'The deepest work per action and the most tokens.' },
};

/** The levels in the contract's order, named as the live pricing page names them. */
export const INTELLIGENCE_LEVEL_OPTIONS = INTELLIGENCE_LEVELS.map((value) => ({
  value,
  ...LEVEL_TEXT[value],
}));

export function intelligenceLevelLabel(level: IntelligenceLevel): string {
  return LEVEL_TEXT[level].label;
}

/** The module's configured level, or null when none (or a value the contract does not know) is set. */
export function intelligenceLevelOf(module: EffectiveModule): IntelligenceLevel | null {
  const parsed = intelligenceLevelSchema.safeParse(module.configuration?.config[INTELLIGENCE_LEVEL_KEY]);
  return parsed.success ? parsed.data : null;
}

const AGENT_MODE_TEXT: Readonly<Record<AgentMode, { label: string; description: string }>> = {
  shadow: { label: 'Shadow', description: 'Reads, searches and drafts. A person sends everything.' },
  supervised: {
    label: 'Supervised',
    description: 'Takes routine steps and asks a person before the steps its tools mark for notice.',
  },
  autopilot: { label: 'Autopilot', description: "Acts within each tool's own safety rules." },
  full_auto: {
    label: 'Full auto',
    description: 'Acts on everything its tools allow. A commitment still needs a person.',
  },
};

/** The agent modes, narrowest first (contracts 0.24.0), as the agent setting offers them for its highest mode. */
export const AGENT_MODE_OPTIONS = AGENT_MODES.map((value) => ({ value, ...AGENT_MODE_TEXT[value] }));

export function agentModeLabel(mode: AgentMode): string {
  return AGENT_MODE_TEXT[mode].label;
}

/** Whether a module's configuration has the agent setting (catalog 2026-10-22.2: every module has it). */
export function hasAgentSetting(definition: Pick<ModuleDefinition, 'configSchema'>): boolean {
  const properties = definition.configSchema?.['properties'];
  return (
    properties !== null && typeof properties === 'object' && MODULE_AGENT_CONFIG_KEYS.enabled in properties
  );
}

/** The default of one property of a module's published configuration schema, when it states one. */
function schemaDefault(definition: Pick<ModuleDefinition, 'configSchema'>, key: string): unknown {
  const properties = definition.configSchema?.['properties'];
  if (properties === null || typeof properties !== 'object') return undefined;
  const property = (properties as Record<string, unknown>)[key];
  return property !== null && typeof property === 'object'
    ? (property as Record<string, unknown>)['default']
    : undefined;
}

/** The reason code platform-api gives a module whose stored configuration its schema no longer accepts. */
export const CONFIGURATION_INVALID_CODE = 'configuration_invalid';

/**
 * Where an agent setting comes from: the organization's configuration states it, the catalog's default stands
 * (the agent keys are stored only when someone set them), or the stored configuration is invalid.
 */
export type AgentSettingSource = 'configured' | 'default' | 'invalid';

export interface AgentSetting {
  enabled: boolean;
  highestMode: AgentMode;
  source: AgentSettingSource;
}

/**
 * What products read for an agent whose configuration cannot be read: off at the narrowest mode (platform-api
 * fails closed, services/platform-api/src/modules/agent-setting.ts). Trace Admin shows the same, so staff never
 * read "On" for an agent that does nothing.
 */
const AGENT_FAILED_CLOSED: Omit<AgentSetting, 'source'> = { enabled: false, highestMode: 'shadow' };

/**
 * Whether the module's stored configuration is one its schema no longer accepts. platform-api says so with the
 * configuration_invalid reason when that is what decides the state; another state (an emergency stop, say) hides
 * that reason, so agent keys the contract does not know count as invalid too.
 */
function hasInvalidConfiguration(module: EffectiveModule): boolean {
  if (module.reasons.some((r) => r.code === CONFIGURATION_INVALID_CODE)) return true;
  const config = module.configuration?.config ?? {};
  const enabled = config[MODULE_AGENT_CONFIG_KEYS.enabled];
  const mode = config[MODULE_AGENT_CONFIG_KEYS.highestMode];
  if (enabled !== undefined && typeof enabled !== 'boolean') return true;
  return mode !== undefined && !agentModeSchema.safeParse(mode).success;
}

/**
 * The module's agent setting as products receive it (GET /v1/internal/organizations/{orgId}/settings):
 * - an invalid stored configuration: off at shadow (fail closed), source invalid;
 * - a configuration that states agentEnabled: its values, source configured;
 * - otherwise (never configured, or a read without the agent keys): the catalog's defaults from the published
 *   schema, source default.
 * platform-api fills the catalog's defaults into a stored configuration that fits its schema, so a read of a stored
 * row always states agentEnabled even when nobody set it. Such a row shows its effective value as configured rather
 * than guessing "(the default)": only a module with no stored configuration is certainly at the default.
 */
export function agentSettingOf(module: EffectiveModule): AgentSetting {
  if (hasInvalidConfiguration(module)) return { ...AGENT_FAILED_CLOSED, source: 'invalid' };
  const config = module.configuration?.config ?? {};
  const stored = config[MODULE_AGENT_CONFIG_KEYS.enabled];
  const enabled =
    typeof stored === 'boolean'
      ? stored
      : schemaDefault(module.module, MODULE_AGENT_CONFIG_KEYS.enabled) === true;
  const mode = agentModeSchema.safeParse(
    config[MODULE_AGENT_CONFIG_KEYS.highestMode] ??
      schemaDefault(module.module, MODULE_AGENT_CONFIG_KEYS.highestMode),
  );
  return {
    enabled,
    highestMode: mode.success ? mode.data : DEFAULT_AGENT_HIGHEST_MODE,
    source: typeof stored === 'boolean' ? 'configured' : 'default',
  };
}

/** The agent setting in a few words: "On, up to Supervised" or "Off". */
export function agentSettingText(setting: { enabled: boolean; highestMode: AgentMode }): string {
  return setting.enabled ? `On, up to ${agentModeLabel(setting.highestMode)}` : 'Off';
}

/**
 * Whether the organization configuration turns the module on (whatever its effective state). A configuration whose
 * enabled is null was stored (an agent setting, say) without the module ever being turned on or off: not on.
 */
export function isEnabled(module: EffectiveModule): boolean {
  return module.configuration?.enabled === true;
}

/** What a stored configuration says about the module being on: null is never turned on or off, not off. */
export function configurationSwitchText(enabled: boolean | null): string {
  if (enabled === null) return 'Never turned on or off';
  return enabled ? 'Turned on' : 'Turned off';
}

/** Turning a module on is offered only when the organization could use it: bought and released. */
export function canEnable(module: EffectiveModule): boolean {
  return !isEnabled(module) && module.state !== 'not_entitled' && module.state !== 'unreleased';
}

/**
 * The enabled modules that depend on `key`, directly or through another module, in catalog order. Disabling
 * `key` disables them too (platform-api records the reason); the confirmation names them.
 */
export function enabledDependents(key: string, modules: readonly EffectiveModule[]): EffectiveModule[] {
  const affected = new Set<string>([key]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const candidate of modules) {
      if (affected.has(candidate.module.key)) continue;
      if (candidate.module.dependsOn.some((dependency) => affected.has(dependency))) {
        affected.add(candidate.module.key);
        grew = true;
      }
    }
  }
  return modules.filter((m) => m.module.key !== key && affected.has(m.module.key) && isEnabled(m));
}

/** The dependencies of a module that are not enabled: enabling it first needs them on. */
export function missingDependencies(
  module: EffectiveModule,
  modules: readonly EffectiveModule[],
): EffectiveModule[] {
  return module.module.dependsOn
    .map((key) => modules.find((m) => m.module.key === key))
    .filter(
      (dependency): dependency is EffectiveModule => dependency !== undefined && !isEnabled(dependency),
    );
}

export interface ModuleStatePresentation {
  label: string;
  tone: RecordStatusTone;
  /** One sentence for the state itself; the server's reasons say why it is so. */
  explanation: string;
}

export const MODULE_STATE_PRESENTATION: Readonly<Record<ModuleState, ModuleStatePresentation>> = {
  not_entitled: {
    label: 'Not purchased',
    tone: 'neutral',
    explanation: 'The organization has not bought this module, or its entitlement has ended.',
  },
  unreleased: {
    label: 'Not released',
    tone: 'neutral',
    explanation:
      'The organization bought it, but it is not released in this environment or to this organization.',
  },
  available: {
    label: 'Available',
    tone: 'info',
    explanation: 'Bought and released, but the organization has not turned it on.',
  },
  enabled: {
    label: 'On',
    tone: 'positive',
    explanation: 'Turned on. Its connections are not required or not checked yet.',
  },
  connected: {
    label: 'Connected',
    tone: 'positive',
    explanation: 'Turned on, and every connection it needs is healthy.',
  },
  degraded: {
    label: 'Degraded',
    tone: 'warning',
    explanation: 'Turned on, but a connection it needs is not healthy.',
  },
  disabled: {
    label: 'Off',
    tone: 'neutral',
    explanation: 'Turned off. Its history is kept.',
  },
  emergency_stopped: {
    label: 'Emergency stop',
    tone: 'danger',
    explanation:
      'Stopped: no new work starts and nothing more is sent, posted or booked until the stop is released.',
  },
  spend_capped: {
    label: 'Spend cap reached',
    tone: 'warning',
    explanation: 'The monthly spend cap is reached, so new work waits until the cap allows it.',
  },
  billing_inactive: {
    label: 'Billing stopped',
    tone: 'danger',
    explanation:
      'The subscription of the contract no longer collects at the payment provider, so no new work starts until billing is restored.',
  },
};

export const REASON_LAYER_LABELS: Readonly<Record<ModuleReason['layer'], string>> = {
  entitlement: 'Entitlement',
  release: 'Release',
  configuration: 'Configuration',
  dependency: 'Dependency',
  prerequisite: 'Connection',
  emergency_stop: 'Emergency stop',
  spend_cap: 'Spend cap',
  billing: 'Billing',
  permission: 'Permission',
};
